/** No whitespace, no control character, no backslash anywhere: browsers strip tabs and newlines and read `\` as `/`, so `/\t/evil.com` would leave the app. */
const IN_APP = /^\/(?![/\\])[^\s\u0000-\u001f\u007f\\]*$/;
const MAX_LENGTH = 2048;
/** A query key that may carry a credential: such a URL is never put in another URL. */
const SECRET_KEY = /token|code|state|key|secret|password|session|auth|jwt|sso/i;

/** A path that is safe to return to: in-app only, checked by pattern and by parsing it against this origin. */
export function inAppPath(value: unknown, origin: string = globalThis.location?.origin ?? 'https://app.invalid'): string | null {
  if (typeof value !== 'string' || value.length > MAX_LENGTH || !IN_APP.test(value)) return null;
  try {
    return new URL(value, origin).origin === origin ? value : null;
  } catch {
    return null;
  }
}

/** The query as it may travel in `from`: dropped whole when any key could hold a credential (invitation, reset, SSO callback links). */
function safeSearch(search: string): string {
  for (const key of new URLSearchParams(search).keys()) if (SECRET_KEY.test(key)) return '';
  return search;
}

/**
 * Where a forced logout (a refresh that failed, a 401) sends the person: the login page, told why and where they were.
 * A full page load drops router state, so both travel in the query. The login page itself and the OAuth callback are not
 * places to come back to.
 */
export function sessionEndedUrl(here: { pathname: string; search: string }): string {
  const from = here.pathname + safeSearch(here.search);
  const back = inAppPath(from) && !here.pathname.startsWith('/auth/') ? `&from=${encodeURIComponent(from)}` : '';
  return `/auth/login?expired=1${back}`;
}

/** What `sessionEndedUrl` put in the query: whether the session ended, and the page to return to (`/` when none). */
export function sessionEndedFrom(search: URLSearchParams): { expired: boolean; from: string } {
  return { expired: search.get('expired') === '1', from: inAppPath(search.get('from')) ?? '/' };
}
